// src/pages/Connector/GoogleDrive/GoogleDriveConnector.js
import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axiosInstance from "../../../utils/axiosInstance";
import { hasPermission } from "../../../utils/permissionUtils";
import { Box, Typography, Alert, Button as MuiButton, CircularProgress, LinearProgress } from '@mui/material'; // Added LinearProgress
import ListGoogleDriveFiles from "./ListGoogleDriveFiles";
import GoogleDriveBrowser from "./GoogleDriveBrowser";

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

export default function GoogleDriveConnector() {
    const [isConnected, setIsConnected] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isConnecting, setIsConnecting] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [error, setError] = useState('');
    const [accessToken, setAccessToken] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const location = useLocation();
    const navigate = useNavigate();
    
    const [isPickerOpen, setIsPickerOpen] = useState(false);

    const { chatbotId : paramsChatbotId } = useParams();

    // Permission states
    const [canView, setCanView] = useState(false);
    const [canModify, setCanModify] = useState(false);
    const [canDelete, setCanDelete] = useState(false);
    const [checkingPermissions, setCheckingPermissions] = useState(true);

    // Categories state
    const [categories, setCategories] = useState([]);

    // Check permissions
    useEffect(() => {
        const checkPermissions = async () => {
            setCheckingPermissions(true);
            try {
                const viewPerm = await hasPermission('connectors', 'view');
                const modifyPerm = await hasPermission('connectors', 'add|edit');
                const deletePerm = await hasPermission('connectors', 'delete');

                setCanView(viewPerm);
                setCanModify(modifyPerm);
                setCanDelete(deletePerm);
            } catch (error) {
                console.error('Error checking permissions:', error);
            } finally {
                setCheckingPermissions(false);
            }
        };

        checkPermissions();
    }, []);

    // Fetch categories
    const fetchCategories = useCallback(async () => {
        if (!paramsChatbotId) return;
        try {
            const params = { chatbot_id: paramsChatbotId };
            const response = await axiosInstance.get('/api/google-drive/categories', { params });
            setCategories(response.data.data);
        } catch (error) {
            console.error("Failed to fetch categories", error);
        }
    }, [paramsChatbotId]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    const checkStatus = useCallback(async (showLoading = true) => {
        if (showLoading) setIsLoading(true);
        setError('');
        
        try {
            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
            const response = await axiosInstance.get(`/api/google-drive/status`, {
                timeout: 30000,
                params
            });
            setIsConnected(response.data.connected);
            setAccessToken(response.data.accessToken || null);
            if (!response.data.connected) {
                const params = new URLSearchParams(location.search);
                if (params.has('error') || params.has('connected')) {
                    navigate(location.pathname, { replace: true });
                }
            }
        } catch (err) {
            console.error("Error checking Google Drive status:", err);
            setError("Failed to check connection status. Please try again.");
            setIsConnected(false);
            setAccessToken(null);
        } finally {
            setIsLoading(false);
        }
    }, [location.pathname, location.search, navigate, paramsChatbotId]);

    useEffect(() => {
        document.title = 'Google Drive Connector | KlonIT';
        const params = new URLSearchParams(location.search);
        if (params.has('connected') && params.get('connected') === 'true') {
            console.log("Successfully connected to Google Drive via redirect.");
            navigate(location.pathname, { replace: true });
            checkStatus(false);
            setRefreshTrigger(prev => prev + 1);
        } else if (params.has('error')) {
             setError(`Authentication failed after redirect: ${params.get('error')}`);
             navigate(location.pathname, { replace: true });
        } else {
            checkStatus();
        }
    }, []);

    const handleConnect = async () => {
        setIsConnecting(true);
        setError('');
        try {
            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
            const response = await axiosInstance.get('/api/google-drive/auth/start', { params });
            if (response.data.authorization_url) {
                window.location.href = response.data.authorization_url;
            } else {
                throw new Error('No authorization URL returned from the server.');
            }
        } catch (err) {
            console.error("Error starting Google Drive authentication:", err);
            setError(err.response?.data?.message || "Failed to initiate connection. Please try again.");
            setIsConnecting(false);
        }
     };

     const handleDisconnect = async () => {
         if (!window.confirm('Are you sure you want to disconnect from Google Drive? Your synced files will remain, but you will need to reconnect to sync new files.')) return;
         setIsConnecting(true);
         setError('');
         try {
             const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
             await axiosInstance.post(`/api/google-drive/disconnect`, {}, { params });
             setIsConnected(false);
             setAccessToken(null);
             setError('Successfully disconnected from Google Drive.');
         } catch (err) {
             console.error("Error disconnecting Google Drive:", err);
             setError(err.response?.data?.message || "Failed to disconnect. Please try again.");
         } finally {
             setIsConnecting(false);
         }
     };

    const handleSync = async (fileIds) => {
        if (!fileIds || fileIds.length === 0) {
            setError("No files selected to sync.");
            return;
        }
        
        // CHANGED: Use isSyncing instead of setIsLoading to keep UI visible
        setIsSyncing(true);
        setError('');
        
        const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};

        try {
            const response = await axiosInstance.post(`/api/google-drive/sync`, { fileIds }, { params });
            console.log("Sync response:", response.data);
            setError(response.data.message || 'Files synced successfully.');
            setRefreshTrigger(prev => prev + 1);
        } catch (err) {
            console.error("Error syncing Google Drive files:", err);
            setError(err.response?.data?.message || "Failed to sync files. Please try again.");
        } finally {
            setIsSyncing(false); // Stop syncing loading state
        }
    };

    const handleOpenPicker = () => {
        if (!accessToken) {
            setError("Access token missing. Please reconnect.");
            checkStatus(false);
            return;
        }
        setIsPickerOpen(true);
    };

    const handleCategoriesUpdated = () => {
        fetchCategories();
    };

    if (checkingPermissions) {
        return <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}><Typography>Checking permissions...</Typography></Box>;
    }
    if (!canView) {
        return <Alert severity="error" sx={{ mt: 6 }}><strong>Access Denied:</strong> You don't have permission to view the Google Drive Connector.</Alert>;
    }

    return (
        <div>
            <main className="content">
                <div className="container-fluid p-0">
                    <div className="mb-3">
                        <h1 className="h3 d-inline align-middle"><strong>Google Drive Connector</strong></h1>
                    </div>
                    {error && <Alert severity={error.includes("Success") || error.includes("successfully") ? "success" : "error"} sx={{mt: 2}} onClose={() => setError("")}>{error}</Alert>}
                    
                    {/* Initial Loading State */}
                    {isLoading && <Typography sx={{p: 2}}>Loading configuration...</Typography>}

                    {!isLoading && !isConnected  && !canModify && (
                        <div className="card"><div className="card-body text-center"><p>You don't have permission to connect to Google Drive. Please contact your administrator.</p></div></div>
                    )}

                    {!isLoading && !isConnected && canModify && (
                        <div className="card">
                            <div className="card-body text-center">
                                <p>Connect your Google Drive account to select files for your chatbot.</p>
                                <button className="btn btn-lg btn-primary" onClick={handleConnect} disabled={isConnecting}>
                                    {isConnecting ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Connecting...</> : <><i className="fa fa-google me-2"></i> Connect to Google Drive</>}
                                </button>
                            </div>
                        </div>
                    )}

                    {!isLoading && isConnected  && (
                         <div className="card">
                            {/* NEW: Linear Progress bar during Sync */}
                            {isSyncing && <LinearProgress />}
                            
                            <div className="card-header">
                                 <div className="row align-items-center">
                                     <div className="col-lg-6">
                                        <p className="mb-0">Select files from your Google Drive to sync with your chatbot.</p>
                                        {isSyncing && <Typography variant="caption" color="primary">Syncing selected files... Please wait.</Typography>}
                                     </div>
                                     <div className="col-lg-6">
                                        {canModify && (
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                                <MuiButton 
                                                    variant="contained" 
                                                    color="primary" 
                                                    onClick={handleOpenPicker} 
                                                    disabled={isLoading || isSyncing} // Disabled during sync
                                                    startIcon={isSyncing ? <CircularProgress size={20} color="inherit" /> : <i className="fa fa-plus" />}
                                                >
                                                    {isSyncing ? 'Syncing...' : 'Select Files/Folders'}
                                                </MuiButton>
                                                <MuiButton 
                                                    variant="outlined" 
                                                    color="error" 
                                                    onClick={handleDisconnect} 
                                                    disabled={isConnecting || isSyncing} 
                                                    startIcon={isConnecting ? <CircularProgress size={20}/> : <i className="fa fa-unlink" />}
                                                >
                                                    {isConnecting ? "Disconnecting..." : "Disconnect"}
                                                </MuiButton>
                                            </Box>
                                        )}
                                    </div>
                                 </div>
                            </div>
                            <div className="card-body">
                                <ListGoogleDriveFiles 
                                    refreshTrigger={refreshTrigger} 
                                    chatbotId={paramsChatbotId} 
                                    permission={{ canView, canModify, canDelete }}
                                    categories={categories}
                                    onCategoriesUpdate={handleCategoriesUpdated}
                                />
                            </div>
                         </div>
                    )}
                </div>
            </main>
            
            <GoogleDriveBrowser 
                isOpen={isPickerOpen}
                onClose={() => setIsPickerOpen(false)}
                accessToken={accessToken}
                onSelect={handleSync}
            />
        </div>
    );
}